import { useTranslation } from 'react-i18next';
import { Section } from '../../../../interfaces/Stocks/Warehouses/sections'
import GenericPromises from '../../../../api/GenericPromises';
import { FormsErrors } from '../../../../hooks/Forms/FormsErrors';
import useSnackBar from '../../../../components/Commons/SnackBar/useSnackBar';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { PrimaryButton } from '../../../../theme/buttons';
import { TextField } from '@mui/material';
import { Header } from '../../../../components/Header';

interface UpdateSectionsProps {
  sectionPayload: Section | undefined,
  isEdit: boolean | undefined,
}

export const UpdateSections = ({
  sectionPayload,
  isEdit,
}: UpdateSectionsProps) => {
  const [t] = useTranslation("global");
  const { GenericPutResource } = GenericPromises();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { GetError } = FormsErrors();
  const [loadingPost, setLoadingPost] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Section>({
    defaultValues: {}
  });
  const onSubmit: SubmitHandler<Section> = (data) => onPut(data);

  const onPut = (data: Section) => {
    let myData = {
      section_name: data.section_name,
      section_description: data.section_description,
      upper_limit_of_items: data.upper_limit_of_items,
      lower_limit_of_items: data.lower_limit_of_items,
      section_location_id: data.section_location_id ?? null,
    }
    setLoadingPost(true);
    GenericPutResource(`/sections/${sectionPayload?.section_id}`, myData)
      .then(() => {
        showSnackBar(t("generic.snackbar.update"), "success");
        setLoadingPost(false);
      })
      .catch((error) => {
        setLoadingPost(false);
        showSnackBar(error.message, 'error');
        setLoadingPost(false);
      });
  }

  useEffect(() => {
    reset({
      section_name: sectionPayload?.section_name,
      section_description: sectionPayload?.section_description,
      upper_limit_of_items: sectionPayload?.upper_limit_of_items,
      lower_limit_of_items: sectionPayload?.lower_limit_of_items,
      section_location_id: sectionPayload?.section_location_id,
    });
    setDataLoaded(true);
  }, []);

  return (
    <>
      {dataLoaded &&
        <>
          <div className="d-flex flex-column w-100 align-items-center mt-2">
            <div className='d-flex justify-content-center custom-card-view w-75'>
              <form onSubmit={handleSubmit(onSubmit)} className='mt-4 w-100'>
                <div className='d-flex'>

                  <div className='w-50'>
                    <Controller
                      name="section_name"
                      control={control}
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field }) =>
                        <TextField
                          variant="filled"
                          label={`${t("sections.fields.section_name")}`}
                          ref={field.ref}
                          value={field.value}
                          onChange={(event) => { field.onChange(event.target.value) }}
                          error={!!errors.section_name}
                          helperText={GetError(errors.section_name?.type)}
                          size="small"
                          style={{ width: "100%" }}
                          sx={{ paddingRight: 2 }}
                          disabled={isEdit}
                        />
                      }
                    />
                  </div>

                  <div className='w-50'>
                    <Controller
                      name="section_description"
                      control={control}
                      rules={{ required: true, maxLength: 200 }}
                      render={({ field }) =>
                        <TextField
                          variant="filled"
                          label={`${t("sections.fields.section_description")}`}
                          ref={field.ref}
                          value={field.value}
                          onChange={(event) => { field.onChange(event.target.value) }}
                          error={!!errors.section_description}
                          helperText={GetError(errors.section_description?.type)}
                          size="small"
                          style={{ width: "100%" }}
                          disabled={isEdit}
                        />
                      }
                    />
                  </div>

                </div>

                <div className='d-flex mt-2'>
                  <Controller
                    name="upper_limit_of_items"
                    control={control}
                    rules={{}}
                    render={({ field }) =>
                      <TextField
                        variant="filled"
                        label={`${t("sections.fields.upper_limit_of_items")}`}
                        type='number'
                        ref={field.ref}
                        sx={{
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                          "& input[type=number]": { MozAppearance: "textfield", }, paddingRight: 2,
                        }}
                        value={field.value}
                        onChange={(event) => { field.onChange(event.target.value) }}
                        error={!!errors.upper_limit_of_items}
                        helperText={GetError(errors.upper_limit_of_items?.type)}
                        size="small"
                        style={{ width: "50%" }}
                        disabled={isEdit}
                      />
                    }
                  />
                  <Controller
                    name="lower_limit_of_items"
                    control={control}
                    rules={{}}
                    render={({ field }) =>
                      <TextField
                        variant="filled"
                        label={`${t("sections.fields.lower_limit_of_items")}`}
                        type='number'
                        ref={field.ref}
                        sx={{
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                          "& input[type=number]": { MozAppearance: "textfield", },
                        }}
                        value={field.value}
                        onChange={(event) => { field.onChange(event.target.value) }}
                        error={!!errors.lower_limit_of_items}
                        helperText={GetError(errors.lower_limit_of_items?.type)}
                        size="small"
                        style={{ width: "50%" }}
                        disabled={isEdit}
                      />
                    }
                  />
                </div>

                {!isEdit &&
                  <div className='d-flex flex-row-reverse mt-4'>
                    <PrimaryButton type='submit' variant='outlined' className='m-1' disabled={loadingPost}>{t("generic.buttons.update")}</PrimaryButton>
                  </div>
                }
              </form>
            </div>

            <SnackbarComponent />
          </div>
        </>
      }
    </>
  )
}
